// src/app/services/map-tooltip-helper.service.ts
import * as L from 'leaflet';
import * as turf from '@turf/turf';

function hasTooltipValue(value: unknown): boolean {
  return value !== undefined && value !== null && String(value).trim() !== '';
}

function escapeHtml(value: unknown): string {
  return String(value).replace(/[&<>"']/g, (ch) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch] as string
  ));
}

// "2026-10-27T06:00:00Z" -> "2026-10-27 06:00"; only ISO values with both T and Z are cleaned, anything else is shown as sent.
export function formatUtcDate(value: unknown): string {
  const text = String(value).trim();
  const match = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})(?::00(?:\.0+)?)?Z$/i.exec(text);
  return match ? `${match[1]} ${match[2]}` : text;
}

// Point-safe hover tooltip for point features (e.g. Oil-spill-prediction forecast points).
// Uses layer.getLatLng() instead of layer.getBounds(), which circleMarker layers don't support.
export function attachForecastPointHoverPopup({
  layer,
  feature,
  map,
  showEeTooltip,
  nudgeInsideView,
  pickDirection,
  onForecastHover,
}: {
  layer: any;
  feature: any;
  map: L.Map;
  showEeTooltip: (lyr: any, direction: 'top' | 'bottom' | 'left' | 'right', content: string, anchor: L.LatLng) => void | L.Tooltip;
  nudgeInsideView: (latLng: L.LatLng, padding?: number) => L.LatLng;
  pickDirection: (pt: L.Point, threshold: number, padding: { right: number; top: number }) => string;
  // Reports the hovered feature's forecast hour (null on mouseout) so the legend can show that hour's area.
  onForecastHover?: (hour: number | null) => void;
}) {
  if (onForecastHover) {
    layer.on('mouseover', () => {
      const hour = Number(feature?.properties?.forecast_h ?? feature?.properties?.forecast_hour);
      onForecastHover(Number.isFinite(hour) ? hour : null);
    });
    layer.on('mouseout', () => onForecastHover(null));
  }

  layer.on('mouseover', (e: L.LeafletMouseEvent) => {
    try {
      const latLng: L.LatLng = layer.getLatLng ? layer.getLatLng() : e.latlng;
      const props = feature.properties ?? {};

      const forecastHour = props.forecast_h ?? props.forecast_hour;
      const forecastTimeUtc = hasTooltipValue(props.fcst_t_utc) ? formatUtcDate(props.fcst_t_utc) : undefined;
      // Flood prediction sends `date` (ISO UTC) and an `area_<unit>` key instead of the oil spill fields.
      const dateUtc = hasTooltipValue(props.date) ? formatUtcDate(props.date) : undefined;
      const detectionTimeUtc = props.det_t_utc;
      const driftDistanceM = props.drift_m ?? props.drift_distance_m;
      const spillKey = Object.keys(props).find((k) => k.startsWith('spill_') && hasTooltipValue(props[k]));
      const spillArea = spillKey ? props[spillKey] : undefined;
      const spillUnit = spillKey ? spillKey.slice('spill_'.length) : '';
      const areaKey = Object.keys(props).find((k) => k.startsWith('area_') && hasTooltipValue(props[k]));
      const area = areaKey ? props[areaKey] : undefined;
      const areaUnit = areaKey ? areaKey.slice('area_'.length) : '';

      const content = `
    <div class="custom-tooltip-content" style="text-transform: none;">
      ${hasTooltipValue(forecastHour) ? `<b>Forecast hour:</b> ${escapeHtml(forecastHour)}h<br>` : ''}
      ${hasTooltipValue(forecastTimeUtc) ? `<b>Forecast time (UTC):</b> ${escapeHtml(forecastTimeUtc)}<br>` : ''}
      ${dateUtc ? `<b>Date (UTC):</b> ${escapeHtml(dateUtc)}<br>` : ''}
      ${hasTooltipValue(detectionTimeUtc) ? `<b>Detection time (UTC):</b> ${escapeHtml(detectionTimeUtc)}<br>` : ''}
      ${hasTooltipValue(driftDistanceM) ? `<b>Drift distance:</b> ${escapeHtml(driftDistanceM)} m<br>` : ''}
      ${spillKey ? `<b>Spill area:</b> ${escapeHtml(spillArea)} ${escapeHtml(spillUnit)}<br>` : ''}
      ${areaKey ? `<b>Area:</b> ${escapeHtml(area)} ${escapeHtml(areaUnit)}<br>` : ''}
      <b>Lng:</b> ${latLng.lng}<br>
      <b>Lat:</b> ${latLng.lat}<br>
    </div>`;

      const safeAnchor = nudgeInsideView(latLng, 80);
      const pt = map.latLngToContainerPoint(safeAnchor);
      const dir = pickDirection(pt, 120, { right: 100, top: 60 });

      showEeTooltip(layer, dir as 'top' | 'bottom' | 'left' | 'right', content, safeAnchor);
    } catch (err) {
    }
  });
}

export function attachVesselHoverPopup({
  layer,
  feature,
  feedName,
  map,
  nonVesselFeed,
  helperService,
  nudgeInsideView,
  pickDirection,
  showEeTooltip,
}: {
  layer: any;
  feature: any;
  feedName: string;
  map: L.Map;
  nonVesselFeed: any[];
  helperService: any;
  nudgeInsideView: (latLng: L.LatLng, padding?: number) => L.LatLng;
  pickDirection: (pt: L.Point, threshold: number, padding: { right: number; top: number }) => string;
  showEeTooltip: (lyr: any, direction: 'top' | 'bottom' | 'left' | 'right', content: string, anchor: L.LatLng) => void | L.Tooltip;
}) {
  layer.on('mouseover', (e: L.LeafletMouseEvent) => {
    if (feature.geometry.type === 'Point' || feature.geometry.type === 'MultiPoint') return;

 try {
  const coords = JSON.parse(JSON.stringify(feature.geometry.coordinates));

  let centroid;

  if (feature.geometry.type === 'MultiPolygon') {
    if (Array.isArray(coords?.[0]?.[0])) {
      coords[0][0].push(coords[0][0][0]);
    }
    centroid = turf.centroid(turf.multiPolygon(coords));
  } 
  else if (feature.geometry.type === 'Polygon') {
    if (Array.isArray(coords?.[0])) {
      coords[0].push(coords[0][0]);
    }
    centroid = turf.centroid(turf.polygon(coords));
  } 
  else if (feature.geometry.type === 'LineString') {
    centroid = turf.centroid(turf.lineString(feature.geometry.coordinates));
  } 
  else {
    centroid = turf.centroid(feature as any);
  }

  const centroidCoordinates = centroid.geometry.coordinates;

  const bounds = layer.getBounds();
  const north = bounds.getNorth();
  const west = bounds.getWest();
  const east = bounds.getEast();
  const labelLatLng = L.latLng(north + 0.00003, (west + east) / 2);
  const area = feature.properties?.area;
  const unit = feature.properties?.unit;
  const displayUnit = String(unit).trim().toLowerCase() === 'sqm' ? 'sqm' : unit;
  const timestamp = feature.properties?.Timestamp ?? feature.properties?.timestamp;
  const seaState = feature.properties?.sea_state;
  const vesselLength =
    feature.properties?.vessel_length ||
    feature.properties?.vessel_len ||
    feature.properties?.length;
  const vehicleLength = feature.properties?.class === 'aircraft' ? null : feature.properties?.length_m;

  const content = `
    <div class="custom-tooltip-content" style="text-transform: none;">
      ${hasTooltipValue(feature.properties?.confidence) ? `<b>Confidence:</b> ${feature.properties?.confidence}<br>` : ''}
      ${hasTooltipValue(vesselLength) ? `<b>Vessel length:</b> ${vesselLength}<br>` : ''}
      ${feature.properties?.class === 'aircraft' && hasTooltipValue(feature.properties?.length_m) ? `<b>Aircraft length:</b> ${feature.properties?.length_m}<br>` : ''}
      ${hasTooltipValue(timestamp) ? `<b>Timestamp:</b> ${timestamp}<br>` : ''}
      ${hasTooltipValue(seaState) ? `<b>Sea state:</b> ${seaState}<br>` : ''}
      ${(feature.properties?.mean_depth_m != null || feature.properties?.mean_depth != null) ? `<b>Mean Depth :</b> ${feature.properties.mean_depth_m ?? feature.properties.mean_depth}<br>` : ''}
      ${vehicleLength != null ? `<b>Length:</b> ${vehicleLength} m<br>` : ''}
      <b>Lng:</b> ${centroidCoordinates[0]}<br>
      <b>Lat:</b> ${centroidCoordinates[1]}<br>
      ${hasTooltipValue(area) ? `<b>Area:</b> ${area}${unit ? ` <span style="text-transform: none;">${String(unit).toLowerCase()}</span>` : ''}<br>` : ''}
    </div>`;

  const { anchor } = helperService.computeAnchorAndLocation(feature, labelLatLng);
  const safeAnchor = nudgeInsideView(anchor, 80);
  const pt = map.latLngToContainerPoint(safeAnchor);
  const dir = pickDirection(pt, 120, { right: 100, top: 60 });

  showEeTooltip(layer, dir as 'top' | 'bottom' | 'left' | 'right', content, safeAnchor);

} catch (err) {
}
  });
}
